import { useEffect, useState, type ReactNode } from 'react';

import { Dialog, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

import { advanceOpenOf, useAllocatedDownpayment } from '../api/use-allocated-downpayment';
import { useAppliedProvisional } from '../api/use-applied-provisional';
import { usePricingReadiness } from '../api/use-pricing-readiness';
import { pricingReadinessBlockers } from '../lib/pricing-readiness-labels';
import type { InvoiceRow } from '../types';
import {
  InvoiceDialogContent,
  InvoiceDialogFormShell,
  InvoiceDialogRightPane,
} from './invoice-dialog-shell';
import { InvoiceCreatePdfProvider, useCreatePdfFileState } from './invoice-create-pdf-context';
import {
  FinalSettlementFormBody,
  useFinalSettlementForm,
  useWatchedTotals,
} from './final-settlement-form';
import { FinalSettlementDialogExtras } from './final-settlement-dialog-extras';
import { ResolvedDocumentSection } from './resolved-document-section';
import type {
  BlContextForTradeInvoice,
  TicketContextForTradeInvoice,
  TradePrefill,
} from './trade-invoice-payload';
import { TradeInvoiceDialogActions } from './trade-invoice-actions';
import { reclassificationReasonMissing } from './trade-invoice-reclassify';
import { useSaveFinalSettlementMutation } from './use-save-trade-mutation';

export interface FinalSettlementInvoiceDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly side: 'buy' | 'sell';
  readonly orderId: string;
  readonly blOrderId: string;
  readonly existingInvoice: InvoiceRow | null;
  readonly ticket: TicketContextForTradeInvoice;
  readonly bl: BlContextForTradeInvoice;
  readonly onSaved: () => void;
  // AI-extraction hand-off: seeded field values plus the extraction linkage
  // recorded on save (mirrors the provisional dialog's prop).
  readonly prefill?: TradePrefill;
}

function buildTitle(side: 'buy' | 'sell', existing: InvoiceRow | null): string {
  const verb = existing !== null ? 'Edit' : 'Create';
  const suffix = side === 'buy' ? 'Purchase Side' : 'Sales Side';
  return `${verb} Final Settlement Invoice — ${suffix}`;
}

function buildDescription(existing: InvoiceRow | null): string {
  return existing !== null
    ? 'Edit any field while the invoice is in Draft. Issuing locks the invoice.'
    : 'Fill in the final settlement invoice details. Saving creates a Draft on this BL.';
}

interface InnerProps extends Omit<FinalSettlementInvoiceDialogProps, 'prefill'> {
  readonly allocatedDownpayment: number;
  readonly advanceOpen: number | null;
  readonly appliedProvisional: number;
  readonly pricingReady: boolean;
  readonly missingFields: string[];
  readonly ticketId: string | null;
  readonly readinessLoaded: boolean;
  readonly prefill: TradePrefill | undefined;
  readonly rightPane: ReactNode;
}

function useOverpaymentAcknowledgement(open: boolean): [boolean, (next: boolean) => void] {
  const [acknowledged, setAcknowledged] = useState(false);
  useEffect(() => {
    if (open) setAcknowledged(false);
  }, [open]);
  return [acknowledged, setAcknowledged];
}

function useFinalSettlementSubmit(
  form: ReturnType<typeof useFinalSettlementForm>,
  props: InnerProps,
) {
  const mutation = useSaveFinalSettlementMutation({
    side: props.side,
    orderId: props.orderId,
    blOrderId: props.blOrderId,
    existingInvoice: props.existingInvoice,
    onSaved: props.onSaved,
    onOpenChange: props.onOpenChange,
    ...(props.side === 'buy' && props.prefill !== undefined
      ? { aiExtractionId: props.prefill.aiExtractionId }
      : {}),
  });
  const onSubmit = form.handleSubmit((values) => {
    if (
      props.existingInvoice !== null &&
      reclassificationReasonMissing(values, 'Final Settlement')
    ) {
      form.setError('reclassification_reason', {
        type: 'required',
        message: 'A reason is required when reclassifying invoice type.',
      });
      return;
    }
    mutation.mutate(values);
  });
  return { mutation, onSubmit };
}

interface FinalSettlementContentProps {
  readonly props: InnerProps;
  readonly form: ReturnType<typeof useFinalSettlementForm>;
  readonly totals: ReturnType<typeof useWatchedTotals>;
  readonly isPending: boolean;
  readonly acknowledged: boolean;
  readonly setAcknowledged: (next: boolean) => void;
}

function FinalSettlementContent({
  props,
  form,
  totals,
  isPending,
  acknowledged,
  setAcknowledged,
}: FinalSettlementContentProps) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>{buildTitle(props.side, props.existingInvoice)}</DialogTitle>
        <DialogDescription>{buildDescription(props.existingInvoice)}</DialogDescription>
      </DialogHeader>
      <FinalSettlementFormBody
        form={form}
        totals={totals}
        isPending={isPending}
        side={props.side}
        blOrderId={props.blOrderId}
        ticket={props.ticket}
        bl={props.bl}
        pricingReady={props.pricingReady}
        missingFields={props.missingFields}
        ticketId={props.ticketId}
        readinessLoaded={props.readinessLoaded}
        acknowledged={acknowledged}
        setAcknowledged={setAcknowledged}
        scheduleLocked={props.existingInvoice !== null}
      />
      <FinalSettlementDialogExtras
        form={form}
        existingInvoice={props.existingInvoice}
        disabled={isPending}
        overpayment={totals.overpayment}
        overpaidAmount={-totals.net}
        side={props.side}
        orderId={props.orderId}
        blOrderId={props.blOrderId}
        blOrderName={props.bl.blOrderName}
        currency={props.ticket.currency}
      />
      <ResolvedDocumentSection invoice={props.existingInvoice} kind="Final_Settlement" />
    </>
  );
}

function DialogFormInner(props: InnerProps) {
  const form = useFinalSettlementForm(props.open, props);
  const [acknowledged, setAcknowledged] = useOverpaymentAcknowledgement(props.open);
  const totals = useWatchedTotals(form.control);
  const { mutation, onSubmit } = useFinalSettlementSubmit(form, props);
  const blockers = [
    ...pricingReadinessBlockers(props),
    ...(totals.overpayment && !acknowledged ? ['Acknowledge the overpayment to continue.'] : []),
  ];
  const saveDisabled = mutation.isPending || blockers.length > 0;
  return (
    <form
      onSubmit={(e) => {
        void onSubmit(e);
      }}
      noValidate
      className="flex min-h-0 flex-1 flex-col"
    >
      <InvoiceDialogFormShell
        rightPane={props.rightPane}
        footer={
          <TradeInvoiceDialogActions
            isPending={mutation.isPending}
            saveDisabled={saveDisabled}
            blockers={blockers}
            submitLabel={props.existingInvoice !== null ? 'Save Changes' : 'Create Final Invoice'}
            onCancel={() => {
              props.onOpenChange(false);
            }}
          />
        }
      >
        <FinalSettlementContent
          props={props}
          form={form}
          totals={totals}
          isPending={mutation.isPending}
          acknowledged={acknowledged}
          setAcknowledged={setAcknowledged}
        />
      </InvoiceDialogFormShell>
    </form>
  );
}

interface DialogBodyProps extends Omit<FinalSettlementInvoiceDialogProps, 'prefill'> {
  readonly prefill: TradePrefill | undefined;
  readonly rightPane: ReactNode;
}

function DialogBody(props: DialogBodyProps) {
  const direction = props.side === 'buy' ? 'Purchase' : 'Sale';
  const allocatedQuery = useAllocatedDownpayment(props.blOrderId, direction);
  const appliedProvQuery = useAppliedProvisional(props.blOrderId, direction);
  const readinessQuery = usePricingReadiness(props.blOrderId, direction);
  const allocated =
    allocatedQuery.data === undefined ? 0 : Number(allocatedQuery.data.allocated_downpayment);
  const advanceOpen = advanceOpenOf(allocatedQuery.data);
  const appliedProv =
    appliedProvQuery.data === undefined ? 0 : Number(appliedProvQuery.data.applied_provisional);
  // Default `pricingReady` to `false` until the readiness check resolves so
  // a fast operator can't submit during the loading window before the gate
  // is known. While loading the blocked-alert is also suppressed so we
  // don't flash an empty list of missing fields.
  const pricingReady =
    readinessQuery.data === undefined ? false : readinessQuery.data.has_required_pricing_inputs;
  const missingFields = readinessQuery.data?.missing_fields ?? [];
  const ticketId = readinessQuery.data?.ticket_id ?? null;
  const readinessLoaded = readinessQuery.data !== undefined;
  return (
    <DialogFormInner
      {...props}
      allocatedDownpayment={allocated}
      advanceOpen={advanceOpen}
      appliedProvisional={appliedProv}
      pricingReady={pricingReady}
      missingFields={missingFields}
      ticketId={ticketId}
      readinessLoaded={readinessLoaded}
    />
  );
}

export function FinalSettlementInvoiceDialog(props: FinalSettlementInvoiceDialogProps) {
  const [pdfFile, setPdfFile] = useCreatePdfFileState(props.open, props.prefill?.file ?? null);
  const existing = props.existingInvoice;
  const rightPane = (
    <InvoiceDialogRightPane
      existingInvoice={existing}
      file={pdfFile}
      onFileChange={setPdfFile}
      side={props.side}
    />
  );
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <InvoiceDialogContent>
        <InvoiceCreatePdfProvider value={{ file: pdfFile }}>
          {props.open && <DialogBody {...props} prefill={props.prefill} rightPane={rightPane} />}
        </InvoiceCreatePdfProvider>
      </InvoiceDialogContent>
    </Dialog>
  );
}
